import { motion } from 'framer-motion';
import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { DetailsPlayerSubmit } from '../Team/DetailsPlayer';

import { BestBuyPlayerTable } from './BestBuy';
import { BestOfMatchdayTable } from './BestOfMatchdayTable';
import { DefaultTable } from './DefaultTable';
import { FixtureTable } from './FixtureTable';
import { IdealTeamTable } from './IdealTeamTable';
import { InvictusTable } from './InvictusTable';
import { PlayerListTransfer } from './PlayerListTransfer';
import { PositionTable } from './PositionTable';
import { ReplacementsTable } from './ReplacementsTable';
import { ScoreLastMatchTable } from './ScoreLastMatchTable';
import { ScoreMyPlayersTable } from './ScoreMyPlayersTable';
import { TarjetometroTable } from './TarjetometroTable';
import { TopPlayersTable } from './TopPlayers';

import Dropdown from '@/components/Dropdown';
import { Loader } from '@/components/Loader';
import { DefaultTabsProps, Tabs } from '@/components/Tabs';
import { TabType } from '@/components/Types';
import useAuth from '@/hooks/useAuth';
import {
  useGetFieldAssistantData,
  useGetScoreLastMatch,
} from '@/hooks/useFieldAssistant';
import { useHandleChangesTabs } from '@/hooks/useHandleChangesTabs';
import { SelectValue } from '@/types';
import { EnumTeamContext, VariantFieldAssistance } from '@/types/team';
import {
  FADE_IN_OUT,
  FIELD_ASSISTANT,
  OPTIONS_ASSISTANT,
  OPTIONS_POSITIONS,
  cn,
  formatMillionBalance,
  lockScroll,
  isF5 as isF5Context,
} from '@/utils';
// import useTourney from '@/hooks/useTourney';
// import { useQuery } from 'react-query';

type ComboboxPositions = {
  optionSelected: SelectValue;
  isVisible: boolean;
};
const initStateCombo: ComboboxPositions = {
  optionSelected: OPTIONS_POSITIONS[0],
  isVisible: false,
};

interface Props extends VariantFieldAssistance {
  showTitle?: boolean;
  context?: EnumTeamContext;
  handleTransferAssistante?: (data: DetailsPlayerSubmit, pin?: string) => void;
}

export const FieldAssistant = React.memo(
  ({
    context,
    showTitle = true,
    variant = 'DEFAULT',
    handleTransferAssistante,
  }: Props) => {
    const { tab, handleTabChanges } = useHandleChangesTabs();

    const [statePositionSelected, setStatePositionSelected] =
      useState<ComboboxPositions>(initStateCombo);
    const [stateComponent, setStateComponent] = useState<SelectValue>(
      OPTIONS_ASSISTANT[0]
    );
    const [replacementsTab, setReplacementsTab] = useState(0);

    const { dataDinamics, isLoadingDinamics, dataStatics, isLoadingStatics } =
      useGetFieldAssistantData();
    const {
      data: dataScoreLastMatch,
      isLoading: isLoadingScoreLastMatch,
      refetch: loadScoreLastMatch,
      status,
    } = useGetScoreLastMatch();

    const { userInitialData } = useAuth();

    const isF5 = useMemo(() => {
      return context ? isF5Context(context) : false;
    }, [context]);

    const matchsDaysAvailables = useMemo(() => {
      return isF5
        ? userInitialData?.fechas_disponibles_f5_obj || []
        : userInitialData?.fechas_disponibles_obj || [];
    }, [
      isF5,
      userInitialData?.fechas_disponibles_f5_obj,
      userInitialData?.fechas_disponibles_obj,
    ]);

    // fijo el scroll cuando abro filtros, lista de jugadores o listado de tranferencia desde ayudante embebido
    useEffect(() => {
      lockScroll(variant === 'EMBED');
      return () => {
        lockScroll();
      };
    }, [variant]);

    // Calculate buenos y baratos data
    const buenosYBaratosData = useMemo(() => {
      if (!dataStatics?.todosLosJugadores) return [];

      return dataStatics.todosLosJugadores
        .map((jugador) => {
          const lastThreeScores = jugador.pAnt
            .filter((score): score is number => score !== null)
            .slice(-3);

          if (lastThreeScores.length < 2) {
            return null;
          }

          const sum = lastThreeScores.reduce((acc, score) => acc + score, 0);
          const average = sum / lastThreeScores.length;

          if (jugador.cotizacion < 3000000 && average >= 9) {
            return {
              idJT: jugador.id,
              promedioUltimasTresFechas: average,
            };
          }

          return null;
        })
        .filter(
          (item): item is { idJT: number; promedioUltimasTresFechas: number } =>
            item !== null
        );
    }, [dataStatics]);

    // deshabilito tabla de posiciones sin datos
    const optionsFieldAssistance = useMemo(() => {
      const newOptions: SelectValue[] = OPTIONS_ASSISTANT.map((opt) => ({
        ...opt,
      }));

      if (
        (dataStatics &&
          dataStatics?.tablasDePosiciones &&
          dataStatics?.tablasDePosiciones[tab]?.lineas?.length === 0) ||
        dataStatics?.faseTorneoActiva.infoZonas.props.ocultarTablaPosiciones ===
          'true'
      ) {
        const posOption = newOptions.find(
          (opt) => opt.value === FIELD_ASSISTANT.POSITIONS
        );
        if (posOption) posOption.disabled = true;
      }

      if (
        (!isF5 && !dataStatics?.equipoIdeal) ||
        (isF5 && !dataStatics?.equipoIdealF5) ||
        !dataStatics?.hayAlgunaFechaPublicada
      ) {
        const idealOption = newOptions.find(
          (opt) => opt.value === FIELD_ASSISTANT.IDEAL_TEAM
        );
        if (idealOption) idealOption.disabled = true;
      }

      return newOptions;
    }, [dataStatics, tab, isF5]);

    const ContentTableSelected = useCallback(() => {
      // selecciono tabla a mostrar segun opcion seleccionada en combo select
      if (dataStatics && dataDinamics) {
        // FIXTURE
        if (stateComponent.value === FIELD_ASSISTANT.FIXTURE) {
          const isInterzonal = dataStatics?.fixture.partidos.every(
            (partido) => partido.data.zona === undefined
          );

          const hideZones =
            dataStatics.faseTorneoActiva.infoZonas.props.ocultarZonas ===
            'true';

          return (
            <FixtureTable
              hideZones={hideZones}
              isInterzonal={isInterzonal}
              variant={variant}
            />
          );
        } else if (stateComponent.value === FIELD_ASSISTANT.POSITIONS) {
          // POSICIONES
          return (
            <PositionTable
              teams={dataStatics.tablasDePosiciones[tab].lineas}
              variant={variant}
            />
          );
        } else if (stateComponent.value === FIELD_ASSISTANT.OUT) {
          // QUEDARON AFUERA
          return (
            <DefaultTable
              dataPlayersProps={dataDinamics?.jugNoJuegan}
              dontSort={true}
              positionSelected={statePositionSelected.optionSelected}
            />
          );
        } else if (stateComponent.value === FIELD_ASSISTANT.INVICTUS) {
          // VALLA INVICTA
          return (
            <InvictusTable
              teams={dataStatics.vallasInvictasClub}
              variant={variant}
            />
          );
        } else if (stateComponent.value === FIELD_ASSISTANT.GOL) {
          // GOLEADORES
          return (
            <DefaultTable
              dataPlayersProps={dataStatics?.goleadores}
              positionSelected={statePositionSelected.optionSelected}
              variant="GOL"
            />
          );
        } else if (stateComponent.value === FIELD_ASSISTANT.EX) {
          // LEY DEL EX
          return (
            <DefaultTable
              dataPlayersProps={dataStatics?.leyDelEx}
              dontSort={true}
              positionSelected={statePositionSelected.optionSelected}
              variant="EX"
            />
          );
        } else if (stateComponent.value === FIELD_ASSISTANT.TARJETOMETRO) {
          return (
            <TarjetometroTable
              dataPlayersProps={dataStatics?.jugTarjetometro || []}
              positionSelected={statePositionSelected.optionSelected}
              variant="TARJETOMETRO"
            />
          );
        } else if (stateComponent.value === FIELD_ASSISTANT.BUENOS_Y_BARATOS) {
          // BUENOS Y BARATOS
          return (
            <BestBuyPlayerTable
              dataPlayersProps={buenosYBaratosData}
              positionSelected={statePositionSelected.optionSelected}
            />
          );
        } else if (stateComponent.value === FIELD_ASSISTANT.TOP_PLAYERS) {
          // JUGADORES FIGURA
          return (
            <TopPlayersTable
              dataPlayersProps={dataStatics?.jugFiguras || []}
              positionSelected={statePositionSelected.optionSelected}
            />
          );
        } else if (stateComponent.value === FIELD_ASSISTANT.IDEAL_TEAM) {
          // EQUIPO IDEAL
          return (
            <IdealTeamTable
              equipoIdeal={
                isF5 ? dataStatics?.equipoIdealF5 : dataStatics?.equipoIdeal
              }
              variant={variant}
            />
          );
        } else if (stateComponent.value === FIELD_ASSISTANT.BEST_OF_MATCHDAY) {
          // MEJORES DE LA FECHA
          return (
            <BestOfMatchdayTable
              players={dataStatics?.mejoresPorPuesto || []}
              positionSelected={statePositionSelected.optionSelected}
              variant="DEFAULT"
            />
          );
        } else if (stateComponent.value === FIELD_ASSISTANT.REPLACEMENTS) {
          // REEMPLAZOS DE LA FECHA
          return (
            <ReplacementsTable
              activeTab={replacementsTab}
              handleTransferAssistante={handleTransferAssistante}
            />
          );
        } else if (
          stateComponent.value === FIELD_ASSISTANT.SCORE &&
          dataScoreLastMatch
        ) {
          return (
            <ScoreLastMatchTable
              matchs={dataScoreLastMatch?.partidos}
              variant={variant}
            />
          );
        } else if (stateComponent.value === FIELD_ASSISTANT.MY_PLAYERS) {
          // PUNTAJE MIS JUGADORES
          return (
            <ScoreMyPlayersTable
              idUltFechaPuntos={userInitialData?.id_ult_fecha_puntos}
              isF5={Boolean(isF5)}
              matchsDaysAvailables={matchsDaysAvailables}
              variant={variant}
            />
          );
        }
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
      dataStatics,
      dataDinamics,
      stateComponent.value,
      dataScoreLastMatch,
      variant,
      tab,
      statePositionSelected.optionSelected,
      isF5,
      matchsDaysAvailables,
      buenosYBaratosData,
      replacementsTab,
    ]);

    // efecto para llamar api data de la ultima fecha
    useEffect(() => {
      if (stateComponent.value === FIELD_ASSISTANT.SCORE) {
        loadScoreLastMatch();
      }
    }, [loadScoreLastMatch, stateComponent, status]);

    // muestro selecto posicion segun opcion de ayudante
    useEffect(() => {
      if (
        stateComponent.value === FIELD_ASSISTANT.OUT ||
        stateComponent.value === FIELD_ASSISTANT.GOL ||
        stateComponent.value === FIELD_ASSISTANT.EX ||
        stateComponent.value === FIELD_ASSISTANT.TARJETOMETRO ||
        stateComponent.value === FIELD_ASSISTANT.BUENOS_Y_BARATOS ||
        stateComponent.value === FIELD_ASSISTANT.TOP_PLAYERS ||
        stateComponent.value === FIELD_ASSISTANT.BEST_OF_MATCHDAY
      ) {
        setStatePositionSelected({
          ...statePositionSelected,
          optionSelected: OPTIONS_POSITIONS[0],
          isVisible: true,
        });
      } else {
        setStatePositionSelected(initStateCombo);
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [stateComponent]);

    const tabs: DefaultTabsProps['tabs'] = [
      { name: 'Zona A' },
      {
        name: 'Zona B',
      },
    ];

    const replacementsTabs: DefaultTabsProps['tabs'] = useMemo(
      () => [
        { name: 'Los que más entraron' },
        { name: 'Los que más salieron' },
      ],
      []
    );

    return (
      <motion.div
        animate={'show'}
        className={cn('flex flex-col min-h-[370px]', {
          'h-screen': variant === 'EMBED',
        })}
        initial={'hide'}
        variants={FADE_IN_OUT}
      >
        <div className="lg:px-0 px-4">
          {showTitle && (
            <h4 className="text-base font-bold lg:text-2xl text-gdt-gray mt-5 lg:mt-[37px] lg:mb-5">
              Ayudante de campo
            </h4>
          )}
          <div className="flex flex-col lg:flex-row lg:space-x-4 lg:mt-0 mt-5 items-center">
            <div className="w-full lg:max-w-[400px]">
              {/* MUESTRO SELECT DE AYUDANTE */}
              <Dropdown
                options={optionsFieldAssistance}
                selected={
                  optionsFieldAssistance.find(
                    (opt) => opt.value === stateComponent.value
                  ) || null
                }
                onChange={(item) => {
                  setStateComponent(item);
                }}
              />
            </div>
            {/* MUESTRO SELECT DE POSICIONES */}
            {statePositionSelected.isVisible && (
              <div className="w-full lg:max-w-[400px] mt-4 lg:mt-0">
                <Dropdown
                  options={OPTIONS_POSITIONS}
                  selected={
                    OPTIONS_POSITIONS[
                      statePositionSelected.optionSelected.value
                    ]
                  }
                  onChange={(item) => {
                    setStatePositionSelected({
                      ...statePositionSelected,
                      optionSelected: item,
                    });
                  }}
                />
              </div>
            )}
            {/* TAB SELECCION DE ZONA PARA FIXTURE SI FUERA REQUERIDO */}
            {dataStatics &&
              stateComponent.value === 2 &&
              dataStatics.tablasDePosiciones.length > 1 && (
                <div className="mt-[10px] lg:mt-0 lg:flex-1 w-full">
                  <Tabs
                    activeTab={tab}
                    className="!h-[48px] mt-2 lg:mt-0"
                    tabs={tabs}
                    onClick={(tab: TabType) => handleTabChanges(tab)}
                  />
                </div>
              )}

            {/* TAB SELECCION DE REEMPLAZOS */}
            {stateComponent.value === FIELD_ASSISTANT.REPLACEMENTS && (
              <div className="mt-[10px] lg:mt-0 lg:flex-1 w-full">
                <Tabs
                  activeTab={replacementsTab}
                  className="!h-[48px] mt-2 lg:mt-0"
                  tabs={replacementsTabs}
                  onClick={(tab: number) => {
                    setReplacementsTab(tab);
                  }}
                />
              </div>
            )}

            {/* HEADER INFO FOR IDEAL TEAM */}
            {stateComponent.value === FIELD_ASSISTANT.IDEAL_TEAM &&
              (isF5
                ? dataStatics?.equipoIdealF5
                : dataStatics?.equipoIdeal) && (
                <div className="flex flex-col lg:flex-row items-center justify-center lg:h-[48px] bg-gdt-blue-xl2 rounded-lg px-6 mt-4 lg:mt-0 lg:ml-4 py-3 lg:py-0 w-full lg:w-auto gap-y-2 lg:gap-y-0">
                  <div className="flex items-center justify-center w-full lg:w-auto">
                    <div className="flex items-center gap-2">
                      <span className="text-xs font-medium text-gdt-gray-2 uppercase">
                        TÁCTICA
                      </span>
                      <span className="text-base font-bold text-gdt-gray-2 text-nowrap">
                        {(isF5
                          ? dataStatics?.equipoIdealF5
                          : dataStatics?.equipoIdeal
                        )?.tactica
                          .replaceAll('_', '-')
                          .replace('t', '')}
                      </span>
                    </div>
                    <div className="h-4 w-[1px] bg-gdt-gray-9 mx-6" />
                    <div className="flex items-center gap-2">
                      <span className="text-xs font-medium text-gdt-gray-2 uppercase">
                        PUNTAJE TOTAL
                      </span>
                      <span className="text-base font-bold text-gdt-blue">
                        {
                          (isF5
                            ? dataStatics?.equipoIdealF5
                            : dataStatics?.equipoIdeal
                          )?.ptsTotal
                        }
                      </span>
                    </div>
                  </div>
                  <div className="hidden lg:block h-4 w-[1px] bg-gdt-gray-9 mx-6" />
                  <div className="flex items-center gap-2">
                    <span className="text-xs font-medium text-gdt-gray-2 uppercase">
                      COTIZACIÓN TOTAL
                    </span>
                    <span className="text-base font-bold text-gdt-gray-2">
                      $
                      {formatMillionBalance(
                        (isF5
                          ? dataStatics?.equipoIdealF5
                          : dataStatics?.equipoIdeal
                        )?.valorTotal || 0
                      )}
                    </span>
                  </div>
                </div>
              )}
          </div>
        </div>
        {
          <Loader
            isVisible={
              isLoadingStatics || isLoadingDinamics || isLoadingScoreLastMatch
            }
            timeout={true}
          />
        }
        <motion.div
          animate={'show'}
          className="lg:min-h-[650px]"
          initial={'hide'}
          variants={FADE_IN_OUT}
        >
          {<ContentTableSelected />}
        </motion.div>
        {/* listado de jugadores de mi equipo para transferir que se llama desde la acción agregar de listado */}
        {handleTransferAssistante && (
          <PlayerListTransfer
            context={context}
            handleTransfer={handleTransferAssistante}
          />
        )}
      </motion.div>
    );
  }
);

FieldAssistant.displayName = 'FieldAssistant';
